import { timeTypeEnum } from '@/utils/enum';
import { formatDiff, setTimeToDate } from '@/utils/moment';
import moment from 'moment';

const TimeCaseEnum = {
	VacationOverTime: 0,
	AcceptedOverTime: 1,
	BeforeOverTime: 2,
	AfterOverTime: 3,
	DelayAfterLeave: 4,
	ArrivalDelay: 5,
	ReturnBeforeLeaveEnd: 6,
	ReturnAfterLeaveEnd: 7,
	EarlyDeparture: 8,
	DepartureBeforeLeaveStart: 9,
	DepartureAfterLeaveStart: 10,
	TimeOutOfWork: 11,
	TakeMoreTimeInLeave: 12,
	Attendance: 13,
	Leave: 14,
};

export const getAttendanceTransformer = (info) => {
	const duration = info.workDayParameters
		.filter((e) => e.case === TimeCaseEnum.Attendance)
		.reduce((p, c) => p + c.duration, 0);
	return {
		...info,
		code: info.employeeBrief?.code,
		id: info.id,
		valid: info.valid,
		name: info.employeeBrief?.arabicFullName,
		employeeId: info.employeeBrief?.employeeId,
		section: info.employeeBrief?.sectionId,
		date: info.date,
		arrivalTime: info.firstFingerPrint.newArrivalTime || info.firstFingerPrint.arrivalTime,
		departureTime: info.lastFingerPrint.newDepartureTime || info.lastFingerPrint.departureTime,
		duration: formatDiff(duration, 'm'),
		delayArrivalInMinute: formatDiff(info.delayArrivalInMinute, 'm'),
		earlyGoInMinute: formatDiff(info.earlyGoInMinute, 'm'),
		overTimeInMinute: formatDiff(info.overTimeInMinute, 'm'),
		unjustifiedTime: formatDiff(info.outOfWork, 'm'),
		durationToAll: formatDiff(duration, 'm') + '/' + formatDiff(info.attendanceDuration, 'm'),
		earlyLeaveToVacation: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.DepartureBeforeLeaveStart)?.duration,
			'm',
		),
		lateArriveFromVacation: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.ReturnAfterLeaveEnd)?.duration,
			'm',
		),
		vacationOverTime: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.TakeMoreTimeInLeave)?.duration,
			'm',
		),
		extraBefore: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.BeforeOverTime)?.duration,
			'm',
		),
		extraAfter: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.AfterOverTime)?.duration,
			'm',
		),
		extraVacation: formatDiff(
			info.workDayParameters?.find((e) => e.case === TimeCaseEnum.VacationOverTime)?.duration,
			'm',
		),

		fingerPrints: info.fingerPrintIds,
		fingerPrintCount: info.fingerPrintIds?.length,
		type: info.fingerPrintStatus,
		isDaily: info.isDaily,
	};
};

export const addAttendanceTypeTransformer = (info) => {
	const permanentSystem = {
		name: info.name,
		allowedDelayInMinute: +info.late.duration || 0,
		earlyDepartureInMinute: info.earlyLeave.duration || 0,
		additionStartAfterInMinute: info.extra.minDuration || 0,
	};
	permanentSystem.delayDiscountStatic = info.late.isStatic ? info.late.value || 0 : 0;
	permanentSystem.delayDiscountRate = !info.late.isStatic ? info.late.value || 0 : 0;

	permanentSystem.absenceDiscountStatic = info.earlyLeave.isStatic ? info.earlyLeave.value || 0 : 0;
	permanentSystem.earlyDiscountRate = !info.earlyLeave.isStatic ? info.earlyLeave.value || 0 : 0;

	permanentSystem.absenceDiscountStatic = info.absent.isStatic ? info.absent.value || 0 : 0;
	permanentSystem.absenceDiscountRate = !info.absent.isStatic ? info.absent.value || 0 : 0;

	permanentSystem.additionValueStatic = info.extra.isStatic ? info.extra.value || 0 : 0;
	permanentSystem.additionValueRate = !info.extra.isStatic ? info.extra.value || 0 : 0;

	permanentSystem.maxOfAdditionPerDayInMinute = info.extra.maxDurationAsDay
		? info.extra.maxDuration || 0
		: 0;
	permanentSystem.maxOfAdditionPerHourInMonth = !info.extra.maxDurationAsDay
		? info.extra.maxDuration || 0
		: 0;

	const dailySystems = [];
	const hybirdSystems = [];

	info.days.forEach((e, i) => {
		if (e.from && e.to)
			dailySystems.push({
				day: i,
				startDate: moment().format('YYYY-MM-DD')+ "T" + e.from + ":00",
				endDate: moment().format('YYYY-MM-DD') + "T" + e.to + ":00",
			});
	});

	if(info.daysHybrid?.length) {
		info.daysHybrid.forEach((e, i) => {
			if (e.hoursOnDay && e.minimumWorkHours && e.workDeadlineHours)
				hybirdSystems.push({
					day: i,
					hoursOnDay: e.hoursOnDay,
					minimumWorkHours: e.minimumWorkHours,
					workDeadlineHours: e.workDeadlineHours
				});
		});
	}

	return { permanentSystem, dailySystems, hybirdSystems };
};

export const AddVacationTransformer = (props) => {
	if (!props.vacation.time) {
		props.vacation.time = '00:00';
	}
	const startTime = setTimeToDate(props.vacation.time, props.vacation.date);

	return {
		employeeId: props.employeeId,
		type: props.type,
		isDaily: props.vacation.timeType === timeTypeEnum.daily,
		date: props.vacation.date,
		durationInDay: props.vacation.timeType === timeTypeEnum.daily ? props.vacation.duration : null,
		durationInMinute: props.vacation.timeType === timeTypeEnum.hourly ? props.vacation.duration : null,
		reason: props.vacation.cause,
		leaveTypeId: props.vacation.type,
		startTime: startTime.toISOString(),
	};
};

export const AddMissionTransformer = (props) => {
	if (!props.mission.time) {
		props.mission.time = '00:00';
	}
	const startTime = setTimeToDate(props.mission.time, props.mission.date);

	return {
		employeeId: props.employeeId,
		type: props.type,
		isDaily: props.mission.timeType === timeTypeEnum.daily,
		date: props.mission.date,
		durationInDay: props.mission.timeType === timeTypeEnum.daily ? props.mission.duration : null,
		durationInMinute: props.mission.timeType === timeTypeEnum.hourly ? props.mission.duration : null,
		reason: props.mission.cause,
		taskTypeId: props.mission.type,
		startTime: startTime.toISOString(),
	};
};
